import * as React from 'react';
import { AreaChip } from '@/components/ui/area-chip';
import type { FrameworkContent } from '@/lib/api/discovery-frameworks';
import type { EditorCtx } from './editor';
import { isActive, plural, rolesText, shown } from './framework-logic';
import { ChangeTag, InlineAdd, ItemCard, RetiredTag } from './parts';

/**
 * The Interview types and Questionnaires tabs (prototype `.type-card`
 * lists). A card opens its side panel; while editing, an add button closes
 * the list. Codes show on every card (Decision 54.3).
 */

export const TAB_SCROLL = 'min-h-0 flex-1 overflow-auto px-6 pb-8 pt-4';
const LIST = 'flex max-w-[980px] flex-col gap-2';

function areaLookup(content: FrameworkContent) {
  return new Map(content.areas.map((a) => [a.id, a]));
}

export function InterviewTypesTab({
  ctx,
  isCurator,
  selectedId,
  onOpen,
  onAdd,
}: {
  ctx: EditorCtx;
  isCurator: boolean;
  selectedId: string | null;
  onOpen: (id: string) => void;
  onAdd: (name: string) => Promise<React.ReactNode | null>;
}) {
  const { content, editing } = ctx;
  const areas = areaLookup(content);
  return (
    <div className={TAB_SCROLL}>
      <div className={LIST}>
        <p className="mb-1 text-meta text-ink-500">
          A type prepares an interview. It is suggested from the person&rsquo;s job title, and fills
          in the areas, questions, things to see on screen, questionnaire and length. Each area also
          brings its own questions (Areas tab).
        </p>
        {shown(content.interview_types, editing).map((t) => {
          const questions = (content.type_questions[t.id] ?? []).filter(isActive);
          const asks = questions.filter((q) => q.kind === 'on_screen').length;
          const questionnaire = content.questionnaires.find((q) => q.id === t.questionnaire_id);
          const roles = content.plan_roles[t.id] ?? [];
          return (
            <ItemCard
              key={t.id}
              code={t.code}
              label={`${t.code} ${t.name}`}
              on={selectedId === t.id}
              retired={t.status === 'retired'}
              onClick={() => onOpen(t.id)}
              aside={
                <>
                  {plural(questions.length - asks, 'question')} · {plural(asks, 'on-screen ask')}
                  <br />
                  {questionnaire
                    ? `${questionnaire.code} questionnaire`
                    : 'No questionnaire'} · {t.duration_minutes} min
                  <br />
                  {roles.length > 0 ? `Starting plan: ${rolesText(roles)}` : 'Added when needed'}
                </>
              }
            >
              <div className="font-semibold text-ink-900">
                {t.name} <ChangeTag change={t.change} />
                {t.status === 'retired' && !t.change ? <RetiredTag /> : null}
              </div>
              <div className="truncate text-meta text-ink-500">
                Suggested for titles with: {t.title_keywords.join(', ') || 'none yet'}
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1">
                {t.area_ids.length > 0 ? (
                  t.area_ids.map((id) => (
                    <AreaChip key={id} title={areas.get(id)?.name}>
                      {areas.get(id)?.label ?? 'Unknown area'}
                    </AreaChip>
                  ))
                ) : (
                  <span className="text-meta text-ink-500">No areas yet</span>
                )}
              </div>
            </ItemCard>
          );
        })}
        {editing ? (
          <InlineAdd
            trigger="button"
            label="Add interview type"
            inputLabel="New interview type name"
            placeholder="Name, e.g. Grants"
            busy={ctx.busy}
            onAdd={onAdd}
          />
        ) : isCurator ? (
          <p className="text-meta text-ink-500">Select Edit to add or change types.</p>
        ) : null}
      </div>
    </div>
  );
}

export function QuestionnairesTab({
  ctx,
  isCurator,
  selectedId,
  onOpen,
  onAdd,
}: {
  ctx: EditorCtx;
  isCurator: boolean;
  selectedId: string | null;
  onOpen: (id: string) => void;
  onAdd: (name: string) => Promise<React.ReactNode | null>;
}) {
  const { content, editing } = ctx;
  const areas = areaLookup(content);
  return (
    <div className={TAB_SCROLL}>
      <div className={LIST}>
        <p className="mb-1 text-meta text-ink-500">
          Questionnaires capture the factual layer: counts, volumes, ratings. They are answered in
          an interview or sent by secure link, and each answer becomes confirmed evidence from the
          person who gave it.
        </p>
        {shown(content.questionnaires, editing).map((q) => {
          const questions = q.questions.filter(isActive);
          const areaIds = Array.from(new Set(questions.flatMap((x) => x.area_ids)));
          const allAreas = questions.some((x) => x.area_ids.length === 0);
          return (
            <ItemCard
              key={q.id}
              code={q.code}
              label={`${q.code} ${q.name}`}
              on={selectedId === q.id}
              retired={q.status === 'retired'}
              onClick={() => onOpen(q.id)}
              aside={
                <>
                  {plural(questions.length, 'question')} · about {q.minutes} min
                  <br />
                  {q.one_per_department
                    ? 'One response per department'
                    : 'In an interview, or by link'}
                </>
              }
            >
              <div className="font-semibold text-ink-900">
                {q.name} <ChangeTag change={q.change} />
                {q.status === 'retired' && !q.change ? <RetiredTag /> : null}
              </div>
              <div className="truncate text-meta text-ink-500">
                {q.audience || 'No audience set'}
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1">
                {areaIds.map((id) => (
                  <AreaChip key={id} title={areas.get(id)?.name}>
                    {areas.get(id)?.label ?? 'Unknown area'}
                  </AreaChip>
                ))}
                {allAreas ? <AreaChip all /> : null}
              </div>
            </ItemCard>
          );
        })}
        {editing ? (
          <InlineAdd
            trigger="button"
            label="Add questionnaire"
            inputLabel="New questionnaire name"
            placeholder="Name, e.g. Grants office"
            busy={ctx.busy}
            onAdd={onAdd}
          />
        ) : isCurator ? (
          <p className="text-meta text-ink-500">Select Edit to add or change questionnaires.</p>
        ) : null}
      </div>
    </div>
  );
}
